import React, { useState } from 'react';
import { useWeb3 } from '../context/Web3Context';
import { Button } from './ui/button';
import { toast } from 'sonner';
import { Wallet, X, Smartphone, Monitor, ExternalLink, Loader2 } from 'lucide-react';

const WalletConnectModal = ({ isOpen, onClose }) => {
  const { connectMetaMask, connectWalletConnect, isConnecting } = useWeb3();
  const [connectingType, setConnectingType] = useState(null);

  if (!isOpen) return null;

  const handleMetaMask = async () => {
    setConnectingType('metamask');
    try {
      await connectMetaMask();
      toast.success('Wallet connected successfully!');
      onClose();
    } catch (error) {
      if (error.message?.includes('not installed')) {
        toast.error('MetaMask not installed. Please install MetaMask or use WalletConnect for mobile wallets.');
      } else {
        toast.error(error.message || 'Failed to connect MetaMask');
      }
    } finally {
      setConnectingType(null);
    }
  };

  const handleWalletConnect = async () => {
    setConnectingType('walletconnect');
    try {
      await connectWalletConnect();
      toast.success('Wallet connected successfully!');
      onClose();
    } catch (error) {
      if (error.message?.includes('User rejected')) {
        toast.error('Connection cancelled');
      } else {
        toast.error(error.message || 'Failed to connect wallet');
      }
    } finally {
      setConnectingType(null);
    }
  };

  const walletOptions = [
    {
      id: 'metamask',
      name: 'MetaMask',
      description: 'Browser extension wallet',
      icon: (
        <svg viewBox="0 0 40 40" className="w-10 h-10">
          <path fill="#E17726" d="M36.0112 3.33325L22.4063 13.2246L24.8144 7.33907L36.0112 3.33325Z"/>
          <path fill="#E27625" d="M3.99325 3.33325L17.4706 13.3305L15.1856 7.33907L3.99325 3.33325Z"/>
          <path fill="#E27625" d="M30.9691 27.2234L27.3424 32.9313L35.2297 35.1172L37.4976 27.3563L30.9691 27.2234Z"/>
          <path fill="#E27625" d="M2.51562 27.3563L4.77082 35.1172L12.6581 32.9313L9.03139 27.2234L2.51562 27.3563Z"/>
          <path fill="#E27625" d="M12.2614 17.5325L10.0708 20.8522L17.5731 21.1951L17.3049 13.0845L12.2614 17.5325Z"/>
          <path fill="#E27625" d="M27.7292 17.5325L22.6157 12.9786L22.4063 21.1951L29.9086 20.8522L27.7292 17.5325Z"/>
          <path fill="#E27625" d="M12.6581 32.9313L17.0816 30.7454L13.2637 27.4021L12.6581 32.9313Z"/>
          <path fill="#E27625" d="M22.9184 30.7454L27.3424 32.9313L26.7363 27.4021L22.9184 30.7454Z"/>
          <path fill="#D5BFB2" d="M27.3424 32.9313L22.9184 30.7454L23.2702 33.8005L23.2291 35.0114L27.3424 32.9313Z"/>
          <path fill="#D5BFB2" d="M12.6581 32.9313L16.7714 35.0114L16.7427 33.8005L17.0816 30.7454L12.6581 32.9313Z"/>
          <path fill="#233447" d="M16.8425 25.4127L13.1535 24.3374L15.7737 23.1621L16.8425 25.4127Z"/>
          <path fill="#233447" d="M23.1575 25.4127L24.2263 23.1621L26.8588 24.3374L23.1575 25.4127Z"/>
          <path fill="#CC6228" d="M12.6581 32.9313L13.2925 27.2234L9.03139 27.3563L12.6581 32.9313Z"/>
          <path fill="#CC6228" d="M26.7081 27.2234L27.3424 32.9313L30.9691 27.3563L26.7081 27.2234Z"/>
          <path fill="#CC6228" d="M29.9086 20.8522L22.4063 21.1951L23.1575 25.4127L24.2263 23.1621L26.8588 24.3374L29.9086 20.8522Z"/>
          <path fill="#CC6228" d="M13.1535 24.3374L15.7737 23.1621L16.8425 25.4127L17.5731 21.1951L10.0708 20.8522L13.1535 24.3374Z"/>
          <path fill="#E27525" d="M10.0708 20.8522L13.2637 27.4021L13.1535 24.3374L10.0708 20.8522Z"/>
          <path fill="#E27525" d="M26.8588 24.3374L26.7363 27.4021L29.9086 20.8522L26.8588 24.3374Z"/>
          <path fill="#E27525" d="M17.5731 21.1951L16.8425 25.4127L17.7525 29.9076L17.9514 23.7562L17.5731 21.1951Z"/>
          <path fill="#E27525" d="M22.4063 21.1951L22.0486 23.7445L22.2475 29.9076L23.1575 25.4127L22.4063 21.1951Z"/>
          <path fill="#F5841F" d="M23.1575 25.4127L22.2475 29.9076L22.9184 30.7454L26.7363 27.4021L26.8588 24.3374L23.1575 25.4127Z"/>
          <path fill="#F5841F" d="M13.1535 24.3374L13.2637 27.4021L17.0816 30.7454L17.7525 29.9076L16.8425 25.4127L13.1535 24.3374Z"/>
          <path fill="#C0AC9D" d="M23.2291 35.0114L23.2702 33.8005L22.9473 33.5223H17.0527L16.7427 33.8005L16.7714 35.0114L12.6581 32.9313L14.0829 34.0949L17.0116 36.1279H22.9884L25.9171 34.0949L27.3424 32.9313L23.2291 35.0114Z"/>
          <path fill="#161616" d="M22.9184 30.7454L22.2475 29.9076H17.7525L17.0816 30.7454L16.7427 33.8005L17.0527 33.5223H22.9473L23.2702 33.8005L22.9184 30.7454Z"/>
          <path fill="#763E1A" d="M36.6715 13.8517L37.8182 8.50082L36.0112 3.33325L22.9184 12.9669L27.7292 17.5325L35.0185 19.6715L36.7548 17.6383L35.9975 17.0945L37.1848 16.0192L36.2579 15.2961L37.4451 14.3963L36.6715 13.8517Z"/>
          <path fill="#763E1A" d="M2.18188 8.50082L3.32856 13.8517L2.54205 14.3963L3.72922 15.2961L2.80228 16.0192L3.98946 17.0945L3.23215 17.6383L4.96845 19.6715L12.2614 17.5325L17.0722 12.9669L3.99325 3.33325L2.18188 8.50082Z"/>
          <path fill="#F5841F" d="M35.0185 19.6715L27.7292 17.5325L29.9086 20.8522L26.7363 27.4021L30.9691 27.3563H37.4976L35.0185 19.6715Z"/>
          <path fill="#F5841F" d="M12.2614 17.5325L4.96845 19.6715L2.51562 27.3563H9.03139L13.2637 27.4021L10.0708 20.8522L12.2614 17.5325Z"/>
          <path fill="#F5841F" d="M22.4063 21.1951L22.9184 12.9669L24.8144 7.33907H15.1856L17.0816 12.9669L17.5731 21.1951L17.7398 23.7679L17.7525 29.9076H22.2475L22.2602 23.7679L22.4063 21.1951Z"/>
        </svg>
      ),
      onClick: handleMetaMask,
      type: 'metamask',
      tag: 'Browser'
    },
    {
      id: 'walletconnect',
      name: 'WalletConnect',
      description: 'Trust Wallet, Rainbow & 100+ wallets',
      icon: (
        <svg viewBox="0 0 40 40" className="w-10 h-10">
          <rect width="40" height="40" rx="8" fill="#3B99FC"/>
          <path d="M12.0667 15.2C16.7556 10.7556 24.0444 10.7556 28.7333 15.2L29.3111 15.7556C29.5556 15.9778 29.5556 16.3556 29.3111 16.5778L27.4667 18.3333C27.3556 18.4444 27.1556 18.4444 27.0444 18.3333L26.2444 17.5778C22.8889 14.3778 17.5111 14.3778 14.1556 17.5778L13.3111 18.3778C13.2 18.4889 13 18.4889 12.8889 18.3778L11.0444 16.6222C10.8 16.4 10.8 16.0222 11.0444 15.8L12.0667 15.2ZM32.5333 18.8L34.1556 20.3556C34.4 20.5778 34.4 20.9556 34.1556 21.1778L27.2889 27.7333C27.0444 27.9556 26.6667 27.9556 26.4444 27.7333L21.5556 23.0222C21.5111 22.9778 21.4222 22.9778 21.3778 23.0222L16.4889 27.7333C16.2444 27.9556 15.8667 27.9556 15.6444 27.7333L8.73333 21.1778C8.48889 20.9556 8.48889 20.5778 8.73333 20.3556L10.3556 18.8C10.6 18.5778 10.9778 18.5778 11.2 18.8L16.0889 23.5111C16.1333 23.5556 16.2222 23.5556 16.2667 23.5111L21.1556 18.8C21.4 18.5778 21.7778 18.5778 22 18.8L26.8889 23.5111C26.9333 23.5556 27.0222 23.5556 27.0667 23.5111L31.9556 18.8C32.1333 18.5778 32.5111 18.5778 32.5333 18.8Z" fill="white"/>
        </svg>
      ),
      onClick: handleWalletConnect,
      type: 'walletconnect',
      tag: 'Mobile'
    }
  ];

  return (
    <div 
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4"
      onClick={onClose}
      data-testid="wallet-modal-overlay"
    >
      <div 
        className="bg-[#0d1d35] border border-[#1a2d4a] rounded-xl max-w-md w-full shadow-2xl"
        onClick={e => e.stopPropagation()}
        data-testid="wallet-modal"
      >
        {/* Header */}
        <div className="flex items-center justify-between p-5 border-b border-[#1a2d4a]">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-[#b8860b]/20 flex items-center justify-center">
              <Wallet className="w-5 h-5 text-[#b8860b]" />
            </div>
            <div>
              <h2 className="text-white font-bold text-lg">Connect Wallet</h2>
              <p className="text-gray-500 text-xs">Choose your preferred wallet</p>
            </div>
          </div>
          <button 
            onClick={onClose}
            className="p-2 rounded-lg hover:bg-[#1a2d4a] transition-colors"
            data-testid="close-modal-btn"
          >
            <X className="w-5 h-5 text-gray-400" />
          </button>
        </div>

        {/* Wallet Options */}
        <div className="p-5 space-y-3">
          {walletOptions.map((wallet) => (
            <button
              key={wallet.id}
              onClick={wallet.onClick}
              disabled={isConnecting}
              className={`w-full flex items-center gap-4 p-4 rounded-xl border transition-all ${
                connectingType === wallet.type
                  ? 'bg-[#b8860b]/20 border-[#b8860b]'
                  : 'bg-[#1a2d4a]/50 border-[#2a3d5a] hover:border-[#b8860b]/50 hover:bg-[#1a2d4a]'
              }`}
              data-testid={`connect-${wallet.id}-btn`}
            >
              <div className="flex-shrink-0">
                {wallet.icon}
              </div>
              <div className="flex-1 text-left">
                <div className="flex items-center gap-2">
                  <span className="text-white font-medium">{wallet.name}</span>
                  <span className="px-2 py-0.5 text-xs rounded bg-[#1a2d4a] text-gray-400">
                    {wallet.tag}
                  </span>
                </div>
                <p className="text-gray-500 text-sm">{wallet.description}</p>
              </div>
              {connectingType === wallet.type ? (
                <Loader2 className="w-5 h-5 text-[#b8860b] animate-spin" />
              ) : (
                <ExternalLink className="w-5 h-5 text-gray-500" />
              )}
            </button>
          ))}
        </div>

        {/* Supported Wallets Info */}
        <div className="px-5 pb-5">
          <div className="bg-[#1a2d4a]/30 rounded-lg p-4">
            <p className="text-xs text-gray-500 text-center">
              <span className="text-[#b8860b]">WalletConnect</span> supports{' '}
              <span className="text-white">Trust Wallet, Rainbow, Coinbase Wallet</span> and 100+ other mobile wallets
            </p>
          </div>
        </div>

        {/* Footer */}
        <div className="px-5 pb-5 text-center">
          <p className="text-xs text-gray-500">
            By connecting, you agree to our{' '}
            <a href="/compliance" className="text-[#b8860b] hover:underline">Terms of Service</a>
          </p>
        </div>
      </div>
    </div>
  );
};

export default WalletConnectModal;
